import { Injectable, Injector, inject, signal } from '@angular/core';
import { AuthService } from '@core/auth.service';
import { firstValueFrom } from 'rxjs';
import type {
  ChallengePresentationOverride,
  PendingChallengeAnimation,
} from '../../models/game.model';
import { GameDataService } from '../../services/GameDataService';
import { ChallengeFlyRendererService } from './challenge-fly-renderer.service';
import { FlyoutRendererService } from './flyout-renderer.service';
import { GameHudService } from './game-hud.service';
import { resolveChallengeLottiePath } from './challenge-lottie-path.util';
import {
  canonicalChallengeType,
  challengeTypeAliases,
} from './challenge-type.util';

@Injectable({ providedIn: 'root' })
export class ChallengeAnimationQueueService {
  private static readonly STALE_BUFFER_MS = 24 * 60 * 60 * 1000;
  private static readonly MAX_ICON_INSTANCES = 5;
  private static readonly CHALLENGE_STAGGER_MS = 250;
  /** Hold "from" state on HUD before fly so mid-sequence is visible on camera. */
  private static readonly FROM_HOLD_MS = 400;
  /** Keep border flash after tween so flash is not truncated by commit. */
  private static readonly BORDER_FLASH_HOLD_MS = 450;
  /** Poll for collapsed/expanded card anchors before starting fly. */
  private static readonly ANCHOR_WAIT_MS = 4000;
  private static readonly ANCHOR_POLL_MS = 100;

  private gameData = inject(GameDataService);
  private flyRenderer = inject(ChallengeFlyRendererService);
  private rewardFlyRenderer = inject(FlyoutRendererService);
  private authService = inject(AuthService);
  private injector = inject(Injector);

  /** Per challenge_type UI overrides while a sequence is playing. */
  readonly presentationOverrides = signal<Record<string, ChallengePresentationOverride>>({});

  /**
   * Sticky progress floor from animation `to.achieved`.
   * Survives override clear so the bar never snaps below the animated target
   * until live `target.progress.achieved` catches up.
   */
  private readonly progressFloors = signal<Record<string, number>>({});

  /**
   * Sticky weekly HUD floor from animation `weekly.to`.
   * Same role as progressFloors — prevents collapsed weekly 1/5 → 0/5 snaps when
   * challenge_hud lags or expand/collapse remounts the count before live catch-up.
   */
  private readonly weeklyProgressFloor = signal(0);

  private running = false;

  /** True while a post-game challenge sequence is in flight (blocks duplicate card-fly). */
  isQueueRunning(): boolean {
    return this.running;
  }

  /** Minimum displayed progress for a challenge type (0 if none). */
  getProgressFloor(challengeType: string): number {
    const floors = this.progressFloors();
    for (const key of challengeTypeAliases(challengeType)) {
      const hit = floors[key];
      if (hit != null) {
        return hit;
      }
    }
    return 0;
  }

  /** Minimum displayed weekly achieved (0 if none). */
  getWeeklyProgressFloor(): number {
    return this.weeklyProgressFloor();
  }

  async runIfNeeded(): Promise<void> {
    if (this.running) {
      return;
    }

    // challenges_new/onboarding settings.completed=false → no time-based fly/progress anims.
    if (this.gameData.isChallengesOnboardingIncomplete()) {
      return;
    }

    const gameHud = this.injector.get(GameHudService);
    if (!gameHud.challengeHudAnimateFlag()) {
      return;
    }

    const pending = [...this.gameData.pendingChallengeAnimations()];
    if (!pending.length) {
      return;
    }

    this.running = true;
    try {
      for (let i = 0; i < pending.length; i++) {
        const item = pending[i];
        try {
          await this.runOne(item);
        } catch (err) {
          this.authService.errorHandlerLogs(
            `ChallengeAnimationQueue error for ${item.id}`,
            err
          );
          this.clearOverride(item.challenge_type);
          await this.commitChallenge(item.id);
        }
        if (i < pending.length - 1) {
          await this.delay(ChallengeAnimationQueueService.CHALLENGE_STAGGER_MS);
        }
      }
    } finally {
      this.presentationOverrides.set({});
      this.running = false;
    }
  }

  getOverride(challengeType: string): ChallengePresentationOverride | null {
    const overrides = this.presentationOverrides();
    for (const key of challengeTypeAliases(challengeType)) {
      const hit = overrides[key];
      if (hit) {
        return hit;
      }
    }
    return null;
  }

  /**
   * Buffered `animation.from` while a challenge is still pending presentation.
   * Used so HUD progress/tick do not jump to live `to` / completed until the
   * post-game queue runs. Returns null once the queue has started that type
   * (override or progress floor) or when nothing is pending.
   */
  getPendingHold(challengeType: string): { achieved: number; completed: boolean } | null {
    if (this.getOverride(challengeType) || this.getProgressFloor(challengeType) > 0) {
      return null;
    }
    const aliases = new Set(challengeTypeAliases(challengeType));
    const pending = this.gameData.pendingChallengeAnimations();
    const hit = pending.find((item) =>
      aliases.has(canonicalChallengeType(String(item.challenge_type ?? '')))
    );
    if (!hit?.animation?.pending || !hit.animation.from) {
      return null;
    }
    return {
      achieved: Number(hit.animation.from.achieved ?? 0) || 0,
      completed: !!hit.animation.from.completed,
    };
  }

  private async runOne(item: PendingChallengeAnimation): Promise<void> {
    const { animation, id } = item;
    // Canonical type + alias override keys so HUD cards (pvp vs competitive) animate.
    const challengeType = canonicalChallengeType(item.challenge_type);
    const fromAchieved = animation.from?.achieved ?? 0;
    const toAchieved = animation.to?.achieved ?? fromAchieved;
    const toCompleted = !!animation.to?.completed;
    const stale = this.isStale(animation.buffered_timestamp);
    const weeklyTo =
      animation.weekly != null
        ? (animation.weekly.to ?? animation.weekly.from)
        : undefined;

    // Progress bar follows toAchieved (and later live target.progress.achieved) — never from.
    this.setProgressFloor(challengeType, toAchieved);
    if (weeklyTo != null && Number.isFinite(weeklyTo)) {
      this.setWeeklyProgressFloor(weeklyTo);
    }
    this.setOverride({
      challengeId: id,
      challengeType,
      displayAchieved: toAchieved,
      displayCompleted: !!animation.from?.completed,
      borderFlash: false,
      showTick: false,
      showGlow: false,
      // Match displayAchieved: hold the committed `to` value — never dip weekly to `from` (0).
      weeklyAchieved: weeklyTo,
      milestoneUnlocked: false,
    });

    if (stale) {
      await this.commitChallenge(id);
      this.clearOverride(challengeType);
      return;
    }

    // Ensure card DOM exists and is in view before fly/flash (AC3 mid-sequence).
    await this.waitForChallengeAnchor(challengeType);
    this.scrollChallengeIntoView(challengeType);
    await this.delay(ChallengeAnimationQueueService.FROM_HOLD_MS);

    const flyUrl = this.resolveFlyAsset(challengeType);
    const iconCount = Math.min(
      Math.max(animation.buffered_progress || toAchieved - fromAchieved || 1, 1),
      ChallengeAnimationQueueService.MAX_ICON_INSTANCES
    );

    // Missing fly asset: skip fly, still tween/commit so HUD never sticks.
    if (flyUrl) {
      for (let n = 0; n < iconCount; n++) {
        await this.flyRenderer.show(challengeType, flyUrl);
      }
    }

    this.patchOverride(challengeType, { borderFlash: true });
    // Keep bar at toAchieved (no from→to step that would dip the value).
    await this.tweenAchieved(challengeType, toAchieved, toAchieved);
    await this.delay(ChallengeAnimationQueueService.BORDER_FLASH_HOLD_MS);
    this.patchOverride(challengeType, { borderFlash: false });

    if (toCompleted) {
      this.patchOverride(challengeType, {
        displayCompleted: true,
        showTick: true,
        showGlow: true,
      });

      // Tick + green glow must finish before weekly/+1 (no overlap).
      const tickPath = resolveChallengeLottiePath(undefined, 'tick.json');
      const glowPath = resolveChallengeLottiePath(undefined, 'glow.json');
      if (tickPath) {
        await this.flyRenderer.showAtAnchor(challengeType, tickPath, 1200);
      }
      if (glowPath) {
        await this.flyRenderer.showGlow(challengeType, 2000, glowPath);
      }
      this.patchOverride(challengeType, { showGlow: false });

      if (animation.weekly) {
        await this.tweenWeekly(
          challengeType,
          animation.weekly.from,
          animation.weekly.to,
          !!animation.weekly.milestone_unlocked
        );
      }

      if (animation.reward?.type) {
        const rewardAsset = this.resolveRewardAsset(animation.reward.type);
        if (rewardAsset) {
          this.rewardFlyRenderer.show(
            animation.reward.type,
            rewardAsset,
            animation.reward.value ?? 0
          );
          // Hold until reward fly settles, then commit so header count updates after fly.
          await this.delay(1200);
        }
      }
    }

    await this.commitChallenge(id);
    // Hold animated progress/tick until challenge_hud catches up — otherwise UI
    // snaps back to stale live values (often 0) the moment override clears.
    const liveCaughtUp = await this.waitForLiveCatchUp(
      challengeType,
      toAchieved,
      toCompleted,
      weeklyTo
    );
    this.clearOverride(challengeType);
    // Only drop floors once live HUD owns the animated values.
    if (liveCaughtUp) {
      this.clearProgressFloor(challengeType);
      if (weeklyTo != null) {
        this.clearWeeklyProgressFloor();
      }
    }
  }

  /** Poll challenge_hud until live progress/completed reflects the animated `to` state. */
  private async waitForLiveCatchUp(
    challengeType: string,
    toAchieved: number,
    toCompleted: boolean,
    weeklyTo?: number
  ): Promise<boolean> {
    const deadline = Date.now() + 3000;
    const aliases = new Set(challengeTypeAliases(challengeType));
    while (Date.now() < deadline) {
      const hudFn = this.gameData.challengeHudData as
        | (() => {
            time_based?: {
              challenges?: Array<{
                challenge_type?: string;
                completed?: boolean;
                target?: { progress?: { achieved?: number; stamp_game_images?: unknown[] } };
              }>;
            };
            weekly?: {
              target?: { progress?: { achieved?: number } };
              weekly_challenge_progress?: number;
            };
          } | null)
        | undefined;
      const hud = hudFn?.();
      const challenges = hud?.time_based?.challenges ?? [];
      const match = challenges.find((c) =>
        aliases.has(canonicalChallengeType(String(c?.challenge_type ?? '')))
      );
      let challengeCaughtUp = false;
      if (match) {
        const stamps = match.target?.progress?.stamp_game_images;
        const stampCount = Array.isArray(stamps) && stamps.length > 0 ? stamps.length : 0;
        const achieved = Number(match.target?.progress?.achieved ?? 0);
        const live = stampCount > 0 ? stampCount : achieved;
        // Completed: HUD flag or live bar already at target.
        if (toCompleted && (match.completed || live >= toAchieved)) {
          challengeCaughtUp = true;
        } else if (!toCompleted && live >= toAchieved) {
          challengeCaughtUp = true;
        }
      }
      let weeklyCaughtUp = weeklyTo == null || !Number.isFinite(weeklyTo);
      if (!weeklyCaughtUp) {
        const weeklyLive = Number(
          hud?.weekly?.target?.progress?.achieved ?? hud?.weekly?.weekly_challenge_progress ?? 0
        );
        const sourceWeekly = this.gameData.weeklyChallengesData?.() as
          | { target?: { hud_progress?: { achieved?: number } } }
          | null
          | undefined;
        const sourceHud = Number(sourceWeekly?.target?.hud_progress?.achieved ?? 0);
        weeklyCaughtUp =
          (Number.isFinite(weeklyLive) && weeklyLive >= (weeklyTo as number)) ||
          (Number.isFinite(sourceHud) && sourceHud >= (weeklyTo as number));
      }
      if (challengeCaughtUp && weeklyCaughtUp) {
        return true;
      }
      await this.delay(100);
    }
    return false;
  }

  private async tweenAchieved(
    challengeType: string,
    _from: number,
    to: number
  ): Promise<void> {
    // Progress bar stays on toAchieved — no from→to dip.
    this.patchOverride(challengeType, { displayAchieved: to });
  }

  private async tweenWeekly(
    challengeType: string,
    from: number,
    to: number,
    milestoneUnlocked: boolean
  ): Promise<void> {
    const steps = Math.max(to - from, 1);
    const stepMs = Math.min(350, Math.floor(700 / steps));
    for (let v = from + 1; v <= to; v++) {
      this.patchOverride(challengeType, {
        weeklyAchieved: v,
        milestoneUnlocked: milestoneUnlocked && v >= to,
      });
      await this.delay(stepMs);
    }
  }

  private resolveFlyAsset(challengeType: string): string | null {
    const map: Record<string, string> = {
      competitive: 'PVP-banner-fly.json',
      luck: 'fortune-banner-fly.json',
      mastery: 'arcade-banner-fly.json',
      pvp: 'PVP-banner-fly.json',
      instant: 'fortune-banner-fly.json',
      endless: 'arcade-banner-fly.json',
    };
    const key = canonicalChallengeType(challengeType);
    const file = map[key] ?? map[challengeType] ?? map.competitive;
    return resolveChallengeLottiePath(file) || null;
  }

  private resolveRewardAsset(rewardType: string): string | null {
    const map: Record<string, string> = {
      star_token: 'prize-tokens-fly.json',
      gems: 'rubies-fly.json',
      points: 'points-fly.json',
      rapid_fire: 'rapidfire-tokens-fly.json',
      rapid_fire_token: 'rapidfire-tokens-fly.json',
    };
    const file = map[rewardType];
    return file ? resolveChallengeLottiePath(file) || null : null;
  }

  private async commitChallenge(challengeId: string): Promise<void> {
    try {
      await firstValueFrom(
        this.authService.getV2CloudFunction('commitchallengeanimation', {
          challenge_id: challengeId,
        })
      );
    } catch (err) {
      this.authService.errorHandlerLogs(
        `commitChallengeAnimation failed for ${challengeId}`,
        err
      );
    }
  }

  private isStale(
    bufferedTimestamp: PendingChallengeAnimation['animation']['buffered_timestamp']
  ): boolean {
    let ms = 0;
    if (typeof bufferedTimestamp === 'number') {
      ms = bufferedTimestamp < 1e12 ? bufferedTimestamp * 1000 : bufferedTimestamp;
    } else if (bufferedTimestamp && typeof bufferedTimestamp === 'object') {
      ms = (bufferedTimestamp.seconds ?? 0) * 1000;
    }
    if (!ms) {
      return false;
    }
    return Date.now() - ms > ChallengeAnimationQueueService.STALE_BUFFER_MS;
  }

  private setProgressFloor(challengeType: string, achieved: number): void {
    const keys = challengeTypeAliases(challengeType);
    this.progressFloors.update((current) => {
      const next = { ...current };
      for (const key of keys) {
        next[key] = Math.max(next[key] ?? 0, achieved);
      }
      return next;
    });
  }

  private clearProgressFloor(challengeType: string): void {
    const keys = challengeTypeAliases(challengeType);
    this.progressFloors.update((current) => {
      const next = { ...current };
      for (const key of keys) {
        delete next[key];
      }
      return next;
    });
  }

  private setWeeklyProgressFloor(achieved: number): void {
    this.weeklyProgressFloor.update((current) => Math.max(current ?? 0, achieved));
  }

  private clearWeeklyProgressFloor(): void {
    this.weeklyProgressFloor.set(0);
  }

  private setOverride(override: ChallengePresentationOverride): void {
    const keys = challengeTypeAliases(override.challengeType);
    this.presentationOverrides.update((current) => {
      const next = { ...current };
      for (const key of keys) {
        next[key] = { ...override, challengeType: key };
      }
      return next;
    });
  }

  private patchOverride(
    challengeType: string,
    patch: Partial<ChallengePresentationOverride>
  ): void {
    const keys = challengeTypeAliases(challengeType);
    this.presentationOverrides.update((current) => {
      const existing =
        keys.map((k) => current[k]).find(Boolean) ?? current[challengeType];
      if (!existing) {
        return current;
      }
      const next = { ...current };
      for (const key of keys) {
        next[key] = { ...existing, ...patch, challengeType: key };
      }
      return next;
    });
  }

  private clearOverride(challengeType: string): void {
    const keys = challengeTypeAliases(challengeType);
    this.presentationOverrides.update((current) => {
      const next = { ...current };
      for (const key of keys) {
        delete next[key];
      }
      return next;
    });
  }

  /** Wait until a challenge card anchor exists (collapsed or expanded HUD). */
  private async waitForChallengeAnchor(challengeType: string): Promise<void> {
    const deadline =
      Date.now() + ChallengeAnimationQueueService.ANCHOR_WAIT_MS;
    while (Date.now() < deadline) {
      if (this.findChallengeAnchor(challengeType)) {
        return;
      }
      await this.delay(ChallengeAnimationQueueService.ANCHOR_POLL_MS);
    }
  }

  private findChallengeAnchor(challengeType: string): HTMLElement | null {
    for (const key of challengeTypeAliases(challengeType)) {
      const el =
        document.querySelector<HTMLElement>(
          `[data-challenge-anchor="${key}"]`
        ) ||
        document.querySelector<HTMLElement>(
          `[data-challenge-fly-end="${key}"]`
        ) ||
        document.querySelector<HTMLElement>(`[data-challenge-icon="${key}"]`);
      if (el) {
        return el;
      }
    }
    return null;
  }

  private scrollChallengeIntoView(challengeType: string): void {
    const el = this.findChallengeAnchor(challengeType);
    if (!el || typeof el.scrollIntoView !== 'function') {
      return;
    }
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    } catch {
      // ignore scroll errors (detached / unsupported)
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
